/**
 * The office's page (operator order 2026-09-10). Route: /provider/attest/:token
 *
 * The link in the plan's email is the credential: no login. Nothing is
 * fetched until the office presses Continue, so a mail scanner never uses
 * the link. The office sees its listing exactly as the directory shows it,
 * presses Confirm, or corrects the fields and sends. Plain words throughout.
 */
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';

const API = process.env.REACT_APP_API_URL || '';

const FIELD_LABELS = {
  practice_name: 'Practice name', street_address: 'Street address', suite: 'Suite or unit', city: 'City', state: 'State', zip_code: 'ZIP',
  phone: 'Office phone', office_email: 'Office email', pcp_accepts_new_patients: 'Accepting new patients', specialty_code: 'Specialty code',
  hospital_affiliations: 'Hospital affiliations', networks: 'Plans and networks you participate in',
  telehealth: 'Offers telehealth', languages: 'Languages spoken by the provider or staff', office_hours: 'Office hours', website: 'Website',
  accessibility: 'Accessibility for members with disabilities', gender: 'Provider gender', board_certification: 'Board certification', accreditation: 'Accreditation',
  // What Florida Medicaid asks about the office for its weekly provider file.
  after_hours: 'Do you see patients after regular hours?', weekend_hours: 'Do you see patients on weekends or holidays?', wheelchair: 'Is the office wheelchair accessible?',
  ehr: 'Does the office use an electronic health record?', age_low: 'Youngest age you see', age_high: 'Oldest age you see',
};
const YN_FIELDS = ['pcp_accepts_new_patients', 'telehealth', 'after_hours', 'weekend_hours', 'wheelchair', 'ehr'];
const AGE_FIELDS = ['age_low', 'age_high'];
const WIDE = ['street_address', 'practice_name', 'hospital_affiliations', 'networks', 'languages', 'office_hours', 'accessibility', 'board_certification', 'accreditation'];
const ageText = (v) => { const m = String(v || '').match(/^(\d{2})([DMY])$/); if (!m) return String(v || '').trim(); const n = Number(m[1]); return m[2] === 'D' ? (n === 0 ? 'Newborn' : `${n} days`) : m[2] === 'M' ? `${n} months` : n === 99 ? 'No upper limit' : `${n} years`; };
const showVal = (f, v) => (YN_FIELDS.includes(f) ? ({ Y: 'Yes', N: 'No' })[v] || '' : f === 'gender' ? ({ M: 'Male', F: 'Female', X: 'Other' })[v] || '' : AGE_FIELDS.includes(f) ? ageText(v) : String(v || '').trim());
// Defined outside the page so React keeps the same tree between renders
// (a component created inside render remounts its inputs on every keystroke).
const Shell = ({ children }) => (
  <div className="min-h-screen bg-slate-50 flex items-start justify-center p-6">
    <div className="bg-white border border-slate-200 rounded-2xl shadow-sm max-w-2xl w-full p-6">{children}</div>
  </div>
);
const fmt = (d) => (d ? new Date(d).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '');

export default function ProviderAttestPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState('view'); // view | correct | done
  const [fields, setFields] = useState({});
  const [note, setNote] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('');
  const [done, setDone] = useState(null);

  const load = async () => {
    setBusy(true); setErr('');
    try {
      const r = await fetch(`${API}/api/roster-attest/${encodeURIComponent(token)}/load`);
      const d = await r.json().catch(() => ({}));
      if (!r.ok || d.success === false) throw new Error(d.message || 'This link could not be opened.');
      setData(d); setFields({ ...d.listing });
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  const submit = async (which) => {
    setBusy(true); setErr('');
    try {
      const body = { mode: which, name, role, fields: which === 'correct' ? { ...fields, note } : {} };
      const r = await fetch(`${API}/api/roster-attest/${encodeURIComponent(token)}/submit`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || d.success === false) throw new Error(d.message || 'Could not save your answer.');
      setDone(d); setMode('done');
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  if (!data) {
    return (
      <Shell>
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Provider directory verification</div>
        <div className="mt-1 text-[17px] font-bold text-slate-900">Confirm your practice listing</div>
        <p className="mt-2 text-[13px] text-slate-600">A health plan has asked your office to confirm how it appears in the plan's provider directory. It takes under a minute. Press Continue to see the listing.</p>
        {err && <p className="mt-3 text-[12px] font-semibold text-rose-600">{err}</p>}
        <button onClick={load} disabled={busy} className="mt-4 px-5 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold disabled:opacity-50">{busy ? 'Opening…' : 'Continue'}</button>
      </Shell>
    );
  }

  const L = data.listing;
  const editable = data.editable || [];
  const changed = editable.filter((f) => String(fields[f] ?? '') !== String(L[f] ?? ''));

  if (mode === 'done' || data.answered) {
    return (
      <Shell>
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{data.org_name} · Provider directory</div>
        <div className="mt-1 text-[17px] font-bold text-slate-900">{done ? done.message : 'This listing was already answered. Thank you.'}</div>
        <p className="mt-2 text-[12.5px] text-slate-600">{L.provider_name} · NPI {L.npi}{data.submitted_at ? ` · answered ${fmt(data.submitted_at)}` : ''}</p>
        {data.contact && (data.contact.contact_name || data.contact.contact_phone || data.contact.contact_email) && (
          <p className="mt-4 text-[12px] text-slate-500">Questions: {[data.contact.contact_name, data.contact.contact_phone, data.contact.contact_email].filter(Boolean).join(', ')}</p>
        )}
      </Shell>
    );
  }

  if (data.expired) {
    return (
      <Shell>
        <div className="text-[17px] font-bold text-slate-900">This link has expired</div>
        <p className="mt-2 text-[13px] text-slate-600">It was valid until {fmt(data.expires_at)}. Please ask {data.org_name} for a new one{data.contact?.contact_email ? ` at ${data.contact.contact_email}` : ''}.</p>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{data.org_name} · Provider directory</div>
      <div className="mt-1 text-[17px] font-bold text-slate-900">{L.provider_name}</div>
      <div className="text-[12px] text-slate-500">NPI {L.npi}{L.practice_name ? ` · ${L.practice_name}` : ''}</div>

      <div className="mt-4 rounded-xl bg-sky-50/60 ring-1 ring-sky-200 px-4 py-3">
        <p className="text-[10px] font-bold uppercase tracking-wider text-sky-800 mb-2">{mode === 'correct' ? 'Type the correct information' : 'How the directory lists this office'}</p>
        <p className="text-[11.5px] text-slate-500 mb-2">Medicare and NCQA require the plan to verify each of these with the office itself: where you see patients, the phone members call, whether you take new patients, telehealth, languages, hospital affiliations and the plans you participate in. Florida Medicaid also asks about after-hours and weekend care, wheelchair access, electronic records and the ages you see.</p>
        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2">
          {editable.map((f) => (
            <label key={f} className={`flex flex-col gap-0.5 ${WIDE.includes(f) ? 'sm:col-span-2' : ''}`}>
              <span className="text-[10.5px] font-semibold text-slate-500">{FIELD_LABELS[f] || f}</span>
              {mode === 'correct' ? (
                YN_FIELDS.includes(f) ? (
                  <select value={fields[f] || ''} onChange={(e) => setFields({ ...fields, [f]: e.target.value })} className="text-[13px] border border-slate-300 rounded-lg px-2 py-1.5 bg-white">
                    <option value="">Not stated</option><option value="Y">Yes</option><option value="N">No</option>
                  </select>
                ) : f === 'gender' ? (
                  <select value={fields[f] || ''} onChange={(e) => setFields({ ...fields, [f]: e.target.value })} className="text-[13px] border border-slate-300 rounded-lg px-2 py-1.5 bg-white">
                    <option value="">Not stated</option><option value="M">Male</option><option value="F">Female</option><option value="X">Other</option>
                  </select>
                ) : AGE_FIELDS.includes(f) ? (
                  <select value={fields[f] || ''} onChange={(e) => setFields({ ...fields, [f]: e.target.value })} className="text-[13px] border border-slate-300 rounded-lg px-2 py-1.5 bg-white">
                    <option value="">Not stated</option>
                    {f === 'age_low' ? <><option value="00D">Newborn</option><option value="01Y">1 year</option><option value="02Y">2 years</option><option value="05Y">5 years</option><option value="12Y">12 years</option><option value="13Y">13 years</option><option value="18Y">18 years</option><option value="21Y">21 years</option><option value="65Y">65 years</option></>
                      : <><option value="99Y">No upper limit</option><option value="17Y">17 years</option><option value="18Y">18 years</option><option value="21Y">21 years</option><option value="64Y">64 years</option></>}
                  </select>
                ) : (
                  <input value={fields[f] || ''} onChange={(e) => setFields({ ...fields, [f]: e.target.value })} className={`text-[13px] border rounded-lg px-2 py-1.5 bg-white ${changed.includes(f) ? 'border-amber-400 bg-amber-50/40' : 'border-slate-300'}`} />
                )
              ) : (
                <span className="text-[13px] text-slate-800 font-medium">{showVal(f, L[f]) || <span className="text-slate-400 italic">{YN_FIELDS.includes(f) || f === 'gender' ? 'not stated' : 'not listed'}</span>}</span>
              )}
            </label>
          ))}
        </div>
        {mode === 'correct' && (
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Anything else the plan should know (optional)"
            className="mt-3 w-full text-[12.5px] border border-slate-300 rounded-lg px-3 py-2 bg-white" />
        )}
      </div>

      <div className="mt-4 grid sm:grid-cols-2 gap-3">
        <label className="flex flex-col gap-0.5"><span className="text-[10.5px] font-semibold text-slate-500">Your name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} className="text-[13px] border border-slate-300 rounded-lg px-2 py-1.5" placeholder="Who is answering" /></label>
        <label className="flex flex-col gap-0.5"><span className="text-[10.5px] font-semibold text-slate-500">Your role</span>
          <input value={role} onChange={(e) => setRole(e.target.value)} className="text-[13px] border border-slate-300 rounded-lg px-2 py-1.5" placeholder="Office manager, front desk…" /></label>
      </div>

      {err && <p className="mt-3 text-[12px] font-semibold text-rose-600">{err}</p>}

      <div className="mt-5 flex items-center gap-3 flex-wrap">
        {mode === 'view' ? (<>
          <button onClick={() => submit('confirm')} disabled={busy || !name.trim()} className="px-5 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-50">Everything is correct. Confirm</button>
          <button onClick={() => setMode('correct')} disabled={busy} className="px-5 py-2.5 rounded-xl border border-slate-300 text-slate-700 hover:border-slate-400 text-sm font-semibold">Something has changed</button>
          <button onClick={() => submit('departed')} disabled={busy || !name.trim()} className="px-5 py-2.5 rounded-xl border border-rose-200 text-rose-700 hover:border-rose-400 text-sm font-semibold disabled:opacity-50">{L.provider_name} no longer practices here</button>
        </>) : (<>
          <button onClick={() => submit('correct')} disabled={busy || !name.trim() || !changed.length} className="px-5 py-2.5 rounded-xl bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold disabled:opacity-50">Send the correction{changed.length ? ` (${changed.length} field${changed.length === 1 ? '' : 's'})` : ''}</button>
          <button onClick={() => { setMode('view'); setFields({ ...L }); }} disabled={busy} className="px-5 py-2.5 rounded-xl border border-slate-300 text-slate-700 text-sm font-semibold">Back</button>
        </>)}
        {!name.trim() && <span className="text-[11.5px] text-slate-500">Please enter your name first.</span>}
      </div>
      <p className="mt-5 text-[10.5px] text-slate-400">This link is for your office only and expires on {fmt(data.expires_at)}. Your answer is recorded with your name, the date and time.{data.contact && (data.contact.contact_phone || data.contact.contact_email) ? ` Questions: ${[data.contact.contact_name, data.contact.contact_phone, data.contact.contact_email].filter(Boolean).join(', ')}.` : ''}</p>
    </Shell>
  );
}
